import { CloudOff, Sun } from 'lucide-react';

/**
 * Ce que le parc PRODUIT vraiment, par opposition à ce qu'il a été déclaré capable de
 * produire.
 *
 * La tuile de puissance installée, juste au-dessus, additionne ce qui a été saisi à
 * l'enrôlement : une valeur statique, que la production ne fait pas bouger. Celle-ci
 * additionne ce que les machines rapportent. David, le 30/08 : « la centrale qui est
 * connectée via Hoymiles, la data ne remonte pas au front ». Elle remontait jusqu'au
 * serveur et s'arrêtait là.
 *
 * LE NOMBRE DE MACHINES MUETTES EST AFFICHÉ AVEC LE TOTAL, et ce n'est pas un détail.
 * Un total incomplet qui se présente comme complet sous-estime le parc sans que
 * personne ne s'en aperçoive — c'est exactement le défaut que cet écran corrige, et il
 * serait absurde de le recommettre à l'échelle du parc.
 *
 * Un parc dont aucune machine ne mesure n'affiche pas « 0 » : « personne ne mesure »
 * et « rien n'a été produit » sont deux faits différents, et seul le second est une
 * information sur la production.
 */
export default function ProductionSummaryPanel({ data }) {
  if (!data) return null;
  const {
    puissance_w: puissance, jour_wh: jour,
    machines_qui_mesurent: mesurantes, machines_sans_mesure: muettes,
  } = data;

  const aucuneMesure = mesurantes === 0;

  return (
    <div className="space-y-3" data-testid="dashboard-production-summary">
      <div>
        <h2 className="text-[15px] font-medium text-neutral-900">Production du parc</h2>
        <p className="text-[13px] text-neutral-400">
          Ce que les machines rapportent, et non ce qui a été déclaré à l&apos;installation.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tuile
          libelle="À l&apos;instant"
          valeur={aucuneMesure ? 'inconnu' : puissanceTexte(puissance)}
          tid="production-parc-instantanee"
          teinte="bg-amber-50 text-amber-600"
          Icone={Sun}
        />
        <Tuile
          libelle="Depuis ce matin"
          valeur={aucuneMesure ? 'inconnu' : energieTexte(jour)}
          tid="production-parc-jour"
          teinte="bg-amber-50 text-amber-600"
          Icone={Sun}
        />
        <Tuile
          libelle="Machines qui mesurent"
          valeur={`${mesurantes} sur ${mesurantes + muettes}`}
          tid="production-parc-couverture"
          teinte={muettes ? 'bg-neutral-100 text-neutral-500' : 'bg-emerald-50 text-emerald-600'}
          Icone={muettes ? CloudOff : Sun}
        />
      </div>

      {muettes > 0 && (
        <p
          className="flex items-start gap-1.5 rounded-md border border-amber-100 bg-amber-50 px-2.5 py-2 text-[13px] text-amber-900"
          data-testid="production-parc-incomplet"
        >
          <CloudOff className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
          <span>
            {muettes === 1
              ? "Un équipement ne rapporte aucune production."
              : `${muettes} équipements ne rapportent aucune production.`}{' '}
            Le total ci-dessus est donc un MINIMUM : la production réelle du parc lui est
            supérieure. Un équipement muet n&apos;est pas un équipement à l&apos;arrêt.
          </span>
        </p>
      )}
    </div>
  );
}

function Tuile({ libelle, valeur, tid, teinte, Icone }) {
  const inconnu = valeur === 'inconnu';
  return (
    <div className="p-4 rounded-lg border border-neutral-200/80 bg-white" data-testid={tid}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${teinte}`}>
          <Icone className="w-4 h-4" />
        </div>
        <p className="text-[16px] font-medium text-neutral-900">{libelle}</p>
      </div>
      <p
        className={`text-[26px] font-semibold ${inconnu ? 'italic text-neutral-400' : 'text-neutral-900'}`}
      >
        {valeur}
      </p>
    </div>
  );
}

/** Des W sous le kilowatt, des kW au-dessus. Une absence s&apos;écrit, elle ne vaut pas 0. */
function puissanceTexte(watts) {
  if (watts == null) return 'inconnu';
  const n = Number(watts);
  if (!Number.isFinite(n)) return 'inconnu';
  if (Math.abs(n) < 1000) return `${Math.round(n)} W`;
  return `${(n / 1000).toFixed(2).replace('.', ',')} kW`;
}

/** Des Wh sous le millier, des kWh au-dessus : personne ne lit « 12200 Wh ». */
function energieTexte(wh) {
  if (wh == null) return 'inconnu';
  const n = Number(wh);
  if (!Number.isFinite(n)) return 'inconnu';
  if (Math.abs(n) < 1000) return `${Math.round(n)} Wh`;
  return `${(n / 1000).toFixed(1).replace('.', ',')} kWh`;
}
